import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const KEY = "cck_geo_v2";
const DEFAULT_RADIUS = 20;
const LocationContext = createContext(null);

const readStored = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "null") || {}; } catch { return {}; }
};

// Listing center point (device GPS or a typed suburb) + radius, remembered across visits.
// perm: "granted" | "denied" | undefined — we only ever trigger the browser prompt once.
export function LocationProvider({ children }) {
  const { user } = useAuth();
  const stored = readStored();
  const [loc, setLocState] = useState(stored.loc || null);
  const [radius, setRadius] = useState(stored.radius || DEFAULT_RADIUS);
  const [status, setStatus] = useState("idle"); // idle|locating|ready|denied|cleared
  const [perm, setPerm] = useState(stored.perm);
  const [manual, setManualFlag] = useState(!!stored.manual);
  const ensuredRef = useRef(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify({ loc, radius, perm, manual }));
  }, [loc, radius, perm, manual]);

  const locateMe = useCallback((silent = false) => {
    if (!navigator.geolocation) {
      setStatus("denied");
      setPerm("denied");
      if (!silent) toast.error("Geolocation is not supported by your browser");
      return;
    }
    setStatus("locating");
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLocState({ lat: coords.latitude, lng: coords.longitude, label: "My current location" });
        setPerm("granted");
        setManualFlag(false);
        setStatus("ready");
        if (!silent) toast.success("Location set — showing listings near you");
      },
      (err) => {
        if (err.code === 1) setPerm("denied");
        setStatus(loc ? "ready" : "denied");
        if (!silent) toast.error(err.code === 1 ? "Location permission denied — enter your suburb instead" : "Could not get your location");
      },
      { enableHighAccuracy: false, maximumAge: 300000, timeout: 10000 }
    );
  }, [loc]);

  const ensure = useCallback(async () => {
    if (ensuredRef.current) return;
    ensuredRef.current = true;
    if (manual && loc) { setStatus("ready"); return; }
    let state = perm;
    try {
      if (navigator.permissions?.query) state = (await navigator.permissions.query({ name: "geolocation" })).state;
    } catch { /* Permissions API unavailable */ }
    if (state === "denied" || (state !== "granted" && perm === "denied")) {
      setPerm("denied");
      setStatus(loc ? "ready" : "denied");
      return;
    }
    locateMe(true);
  }, [manual, loc, perm, locateMe]);

  useEffect(() => { if (user) ensure(); }, [user, ensure]);

  const setManual = useCallback(async (text) => {
    const q = (text || "").trim();
    if (q.length < 3) return;
    setStatus("locating");
    try {
      const { data } = await api.get(`/geocode?q=${encodeURIComponent(q)}`);
      setLocState({ lat: data.lat, lng: data.lng, label: data.label.split(",")[0] });
      setManualFlag(true);
      setStatus("ready");
      toast.success(`Location set: ${data.label.split(",").slice(0, 2).join(",")}`);
    } catch (e) {
      setStatus(loc ? "ready" : "denied");
      toast.error(errMsg(e, "Location not found"));
    }
  }, [loc]);

  const setLoc = useCallback((value) => {
    setLocState(value);
    setManualFlag(!!value);
    setStatus(value ? "ready" : "cleared");
  }, []);

  return (
    <LocationContext.Provider value={{ loc, setLoc, radius, setRadius, status, perm, locateMe, setManual, ensure }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useSharedLocation() {
  const ctx = useContext(LocationContext);
  useEffect(() => { ctx.ensure(); }, [ctx.ensure]); // eslint-disable-line react-hooks/exhaustive-deps
  return {
    ...ctx,
    locating: ctx.status === "locating",
    denied: !ctx.loc && (ctx.status === "denied" || (ctx.perm === "denied" && ctx.status !== "locating")),
  };
}
